
































































































import Vue, { PropType } from 'vue';

// Data
import countries from 'highered-utils/data/countries.json';

// Types and services
import CandidateService, { Candidate } from '~/services/CandidateService';
import CompanyService from '~/services/CompanyService';
import DialogService from '~/services/DialogService';
import { CandidateStudyProgram } from '~/services/CSPService';
import { commonProps } from '~/utils/candidate';
import {
    CandidateProfileScore,
    CandidateProfileScorer,
    CandidateProfileStatus,
} from '~/utils/candidate/CandidateProfileScorer';
import { ArrayItem } from '~/utils/types';

// Components
import Avatar from '~/components/builders/Avatar.vue';
import AttributeIcon, { AttributeIconType } from '../components/AttributeIcon.vue';
import PersonalDetailsModal from '../modals/PersonalDetailsModal.vue';
import Card from './Card.vue';

const countryMap = countries.reduce(
    (map, country) => map.set(country.value, country),
    new Map<string, ArrayItem<typeof countries>>()
);

interface Experience {
    schoolId: number;
    school: string;
    program: string;
    workExperience: string;
}

export default Vue.extend({
    name: 'PersonalDetails',
    components: {
        Card,
        Avatar,
        PersonalDetailsModal,
        AttributeIcon,
    },
    props: {
        ...commonProps(),
        score: {
            type: Object as PropType<CandidateProfileScore | null>,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            service: new CandidateService(),
            showModal: false,

            schoolLogo: '',
            discover: this.candidate.profileVisibility === 'public_to_recruiters',
        };
    },
    computed: {
        fullName(): string {
            return [this.user.firstName, this.user.lastName].filter(x => !!x).join(' ') || 'You';
        },
        nationality(): string | null {
            if (this.candidate.nationalities.length === 0) return null;
            const country = countryMap.get(this.candidate.nationalities[0].nationality || '');
            if (!country) return null;
            return country.label;
        },
        location(): string | null {
            const country = countryMap.get(this.candidate.country || '');
            if (!country) return null;
            return country.label;
        },
        pronoun(): string | null {
            if (!this.candidate.pronoun) return null;
            if (this.$te(`profile.personalDetails.pronounOptions.${this.candidate.pronoun.type}`))
                return this.$t(
                    `profile.personalDetails.pronounOptions.${this.candidate.pronoun.type}`
                ).toString();
            return this.candidate.pronoun.value || null;
        },
        experience(): Experience | null {
            const experience: Partial<Experience> = {};

            // Add Study Program
            const program = this.getLatestStudyProgram();
            if (program && program.studyProgram && program.studyProgram.level) {
                const end = new Date(program.end);
                const programString = this.$t('profile.personalDetails.studyProgram', {
                    year: end.getFullYear(),
                    level: program.studyProgram.level.name,
                    program: program.studyProgram.name,
                }).toString();

                experience.schoolId = program.studyProgram.schoolId || -1;
                experience.school =
                    (!!program.studyProgram.school
                        ? program.studyProgram.school.name
                        : program.studyProgram.customSchoolName) || '';
                experience.program = programString;
            }

            // Add years of work experience
            const numberOfYears = this.calculateTotalYearsOfWorkExperience;
            if (numberOfYears > 0) {
                experience.workExperience = this.$t('profile.personalDetails.numberOfYears', {
                    years: numberOfYears,
                }).toString();
            }

            return Object.keys(experience).length > 0 ? (experience as Experience) : null;
        },
        badges(): AttributeIconType[] {
            const icons: Record<AttributeIconType, boolean> = {
                job: !!this.candidate.preferences.workPreferences.length,
                flex: !['onsite', null].includes(this.candidate.preferences.eligibleWorkLocation),
                relo: !!this.candidate.preferences.eligibleForRelocation,
                lgb: !!this.candidate.preferences.memberOfLGBTQCommunity,
                eth: this.candidate.ethnicMinority === 'yes',
                pwd: this.candidate.disability === 'yes',
            };
            return Object.keys(icons).filter(
                key => icons[key as keyof typeof icons]
            ) as AttributeIconType[];
        },
        beDiscoveredHoverText(): string {
            return this.discover
                ? this.$t('profile.personalDetails.beDiscoveredModeTooltipActive').toString()
                : this.$t('profile.personalDetails.beDiscoveredModeTooltipInactive').toString();
        },
        calculateTotalYearsOfWorkExperience(): number {
            const experiences = this.candidate.workExperiences || [];
            let totalYears = 0;
            const now = new Date();

            for (let i = 0; i < experiences.length; i++) {
                const curWE = experiences[i];
                const endDate = curWE.end ? curWE.end : now;
                if (endDate.getTime() > now.getTime()) endDate.setTime(now.getTime());

                let currentDuration = endDate.getTime() - curWE.start.getTime();

                // Check for overlapping work experiences
                for (let j = i + 1; j < experiences.length; j++) {
                    const otherExperience = experiences[j];
                    const otherEndDate = otherExperience.end ? otherExperience.end : now;
                    if (otherEndDate.getTime() > now.getTime()) otherEndDate.setTime(now.getTime());

                    const overlapStart = Math.max(
                        curWE.start.getTime(),
                        otherExperience.start.getTime()
                    );
                    const overlapEnd = Math.min(endDate.getTime(), otherEndDate.getTime());
                    const overlapDuration = Math.max(0, overlapEnd - overlapStart);

                    // If there is an overlap, subtract the overlap duration from the current duration
                    if (overlapDuration > 0) {
                        currentDuration -= overlapDuration;
                    }
                }
                totalYears += currentDuration / (1000 * 60 * 60 * 24 * 365);
            }
            return Math.round(totalYears);
        },
    },
    methods: {
        getLatestStudyProgram(): CandidateStudyProgram | null {
            const { studyPrograms } = this.candidate;
            if (!studyPrograms || !studyPrograms.length) return null;

            return studyPrograms.reduce<CandidateStudyProgram>((prev, cur) => {
                const endDate = new Date(cur.end);
                if (endDate > new Date(prev.end)) return cur;
                return prev;
            }, studyPrograms[0]);
        },
        async loadSchoolLogo() {
            const program = this.getLatestStudyProgram();
            if (!program || !program.studyProgram.schoolId) return;

            const company = await CompanyService.getCompany(program.studyProgram.schoolId);
            if (!company.data.logo) return;

            this.schoolLogo = company.data.logo;
        },

        handleDiscover() {
            if (!this.discover) {
                // Check if the profile is complete to enable "be discovered" mode
                const scorer = new CandidateProfileScorer(
                    this.candidate,
                    this.user,
                    this.workAuthorizations
                );
                const score = scorer.getBeDiscoveredScore();
                if (score.status === CandidateProfileStatus.INCOMPLETE) {
                    DialogService.openMyProfileBeDiscoveredDialog(scorer);
                    return;
                }
            }

            const profileVisibility = this.discover ? 'private' : 'public_to_recruiters';
            this.discover = !this.discover;

            this.service.updateCandidateProfile({
                userId: this.user.id,
                candidate: {
                    op: 'update',
                    value: { profileVisibility },
                },
            });
        },
        handleGenerateCV() {},
        handleOpenPersonalDetailsModalEvent() {
            this.showModal = true;
        },
    },
    mounted() {
        this.loadSchoolLogo();

        window.addEventListener(
            'candidate:profile:details',
            this.handleOpenPersonalDetailsModalEvent
        );
    },
    beforeDestroy() {
        window.removeEventListener(
            'candidate:profile:details',
            this.handleOpenPersonalDetailsModalEvent
        );
    },
    watch: {
        candidate(value: Candidate) {
            this.discover = value.profileVisibility === 'public_to_recruiters';
        },
    },
});
